import { useEffect, useRef } from 'react'
import { sdk } from '@/lib/sdk'
import type { CheckoutOutcome } from '@/lib/checkout/outcome'

const POLL_INTERVAL_MS = 5000

/**
 * Following a pending purchase: the webhook marks it COMPLETED when the PIX or boleto is paid, and the
 * screen should notice without a reload. Calls `onSettled` with `paid` or `refused`; stops when the
 * purchase settles, when the PIX expires, or on unmount. Shared by the checkout and the plan change.
 */
export function usePendingPurchasePoll(outcome: CheckoutOutcome | null, onSettled: (next: CheckoutOutcome) => void) {
  const pollRef = useRef<number | null>(null)
  const settledRef = useRef(onSettled)
  settledRef.current = onSettled
  useEffect(() => {
    const purchaseId = outcome?.purchaseId
    const waiting = outcome && (outcome.kind === 'pix' || outcome.kind === 'boleto' || outcome.kind === 'processing')
    if (!waiting || !purchaseId) return

    const expiresAt = outcome.kind === 'pix' && outcome.expiresAt ? new Date(outcome.expiresAt).getTime() : null

    pollRef.current = window.setInterval(async () => {
      if (expiresAt && Date.now() > expiresAt) {
        if (pollRef.current) window.clearInterval(pollRef.current)
        return
      }
      try {
        const purchase = await sdk.checkout.getPurchase(purchaseId)
        if (purchase?.status === 'COMPLETED') settledRef.current({ kind: 'paid', purchaseId })
        else if (purchase?.status === 'FAILED') settledRef.current({ kind: 'refused', purchaseId })
      } catch {
        /* A failed check is not a failed payment; try again next tick. */
      }
    }, POLL_INTERVAL_MS)

    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current)
    }
  }, [outcome])
}
